import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { cancelJob, useJobStatus } from '../hooks/useJobStatus';
/**
 * Get status badge color based on job status
 */ function getStatusColor(status) {
    switch(status){
        case 'completed':
            return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
        case 'failed':
            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
        case 'cancelled':
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
        case 'running':
            return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
        case 'pending':
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
        default:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
}
/**
 * Get status display text
 */ function getStatusText(status) {
    switch(status){
        case 'completed':
            return 'Completed';
        case 'failed':
            return 'Failed';
        case 'cancelled':
            return 'Cancelled';
        case 'running':
            return 'Processing';
        case 'pending':
            return 'Queued';
        default:
            return 'Unknown';
    }
}
/**
 * JobStatus Component
 *
 * Displays real-time job processing status with progress bar and current step.
 * Automatically polls job status until completion or failure.
 *
 * Features:
 * - Real-time progress updates with percentage
 * - Current step display (e.g., "Extracting text from PDF...")
 * - Status badges (completed, failed, running, pending)
 * - Cancel button for running jobs
 * - Error display
 * - Responsive design with Tailwind CSS
 *
 * @example
 * ```tsx
 * <JobStatus
 *   jobId={attachment.jobId}
 *   onComplete={() => {
 *     console.log('Job completed!');
 *     // Enable download button, refresh data, etc.
 *   }}
 *   onError={(error) => {
 *     console.error('Job failed:', error);
 *   }}
 * />
 * ```
 */ export const JobStatus = ({ jobId, enabled = true, showCancel = true, onComplete, onError, onCancel, className = '', fetcher })=>{
    const [isCancelling, setIsCancelling] = React.useState(false);
    const { data: jobStatus, isLoading, error, isPolling, status, progress, estimatedRemainingSeconds } = useJobStatus(jobId, {
        enabled,
        onComplete,
        onError,
        onCancel,
        fetcher
    });
    // Track elapsed wall time while the job is polling. We prefer the
    // processor-reported ETA when it's non-zero, but reverie may not have a
    // historical baseline for this job class yet — in that case ETA arrives as
    // 0/undefined and we fall back to showing elapsed wall time so the user
    // still sees the spinner ticking. Reset whenever jobId changes so
    // re-uploads / retries start the clock over.
    const [elapsedSeconds, setElapsedSeconds] = React.useState(0);
    React.useEffect(()=>{
        setElapsedSeconds(0);
        if (!jobId || !isPolling) return;
        const start = Date.now();
        const tick = ()=>setElapsedSeconds(Math.floor((Date.now() - start) / 1000));
        const id = window.setInterval(tick, 1000);
        return ()=>window.clearInterval(id);
    }, [
        jobId,
        isPolling
    ]);
    // The backend stamps `estimatedRemainingSeconds` whenever reverie's
    // status/progress changes — which can be every few seconds or longer.
    // Between stamps we decay the displayed value locally so the user sees a
    // countdown rather than a frozen number. When a fresh ETA arrives we
    // snap to the new baseline (the backend has a better view of remaining
    // work than our linear extrapolation).
    const [etaDisplaySeconds, setEtaDisplaySeconds] = React.useState(undefined);
    const etaBaselineRef = React.useRef(null);
    React.useEffect(()=>{
        if (!estimatedRemainingSeconds || estimatedRemainingSeconds <= 0) {
            etaBaselineRef.current = null;
            setEtaDisplaySeconds(undefined);
            return;
        }
        etaBaselineRef.current = {
            value: estimatedRemainingSeconds,
            capturedAt: Date.now()
        };
        setEtaDisplaySeconds(estimatedRemainingSeconds);
    }, [
        estimatedRemainingSeconds
    ]);
    React.useEffect(()=>{
        if (!isPolling || !etaBaselineRef.current) return;
        const tick = ()=>{
            const baseline = etaBaselineRef.current;
            if (!baseline) return;
            const decayed = Math.max(0, baseline.value - Math.floor((Date.now() - baseline.capturedAt) / 1000));
            setEtaDisplaySeconds(decayed);
        };
        const id = window.setInterval(tick, 1000);
        return ()=>window.clearInterval(id);
    }, [
        isPolling,
        estimatedRemainingSeconds
    ]);
    const handleCancel = async ()=>{
        if (!jobId || isCancelling) return;
        setIsCancelling(true);
        try {
            await cancelJob(jobId);
        // The status will update automatically via polling
        } catch (err) {
            console.error('Failed to cancel job:', err);
            alert(err instanceof Error ? err.message : 'Failed to cancel job');
        } finally{
            setIsCancelling(false);
        }
    };
    // Don't render if no job ID
    if (!jobId || jobId === '' || jobId === 'none') {
        return null;
    }
    // Loading state
    if (isLoading) {
        return /*#__PURE__*/ _jsxs("div", {
            className: `flex items-center space-x-2 ${className}`,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: "text-sm text-gray-600 dark:text-gray-400",
                    children: "Loading status..."
                })
            ]
        });
    }
    // Error state
    if (error) {
        return /*#__PURE__*/ _jsx("div", {
            className: `rounded-md bg-red-50 p-3 dark:bg-red-900/20 ${className}`,
            children: /*#__PURE__*/ _jsxs("div", {
                className: "flex",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-shrink-0",
                        children: /*#__PURE__*/ _jsxs("svg", {
                            className: "h-5 w-5 text-red-400",
                            fill: "currentColor",
                            viewBox: "0 0 20 20",
                            "aria-label": "Error icon",
                            children: [
                                /*#__PURE__*/ _jsx("title", {
                                    children: "Error"
                                }),
                                /*#__PURE__*/ _jsx("path", {
                                    fillRule: "evenodd",
                                    d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z",
                                    clipRule: "evenodd"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "ml-3",
                        children: /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-red-800 dark:text-red-200",
                            children: error instanceof Error ? error.message : 'Failed to load job status'
                        })
                    })
                ]
            })
        });
    }
    // No data state
    if (!jobStatus) {
        return null;
    }
    const showProgressBar = isPolling && progress && progress.percentage > 0;
    const showCancelButton = showCancel && (status === 'pending' || status === 'running');
    // Format a whole-second count as "Ns" under a minute, "Nm Ns" above.
    // Used for both the ETA readout (when reverie reports one) and the
    // elapsed-time fallback (when no ETA is available).
    const formatSeconds = (secs)=>secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
    // We have three readouts. In priority order:
    //   1. Live ETA countdown — backend reported a non-zero ETA AND our local
    //      decay hasn't run it to zero yet.
    //   2. Overshoot — the backend stamped an ETA at some point this session
    //      (etaBaselineRef.current is set) but our decay has reached zero
    //      while polling is still active. The estimate was off; we say so
    //      honestly rather than silently flipping back to elapsed (which
    //      looks like the ETA broke).
    //   3. Pure elapsed — the backend never produced an ETA for this job
    //      (insufficient historical samples for this class), so the spinner
    //      gets a wall-clock companion instead.
    const hasEverHadEta = etaBaselineRef.current !== null;
    let remainingLabel;
    let etaSource;
    if (etaDisplaySeconds !== undefined && etaDisplaySeconds > 0) {
        remainingLabel = `~${formatSeconds(etaDisplaySeconds)} left`;
        etaSource = 'eta';
    } else if (hasEverHadEta) {
        remainingLabel = 'almost done…';
        etaSource = 'overshoot';
    } else {
        remainingLabel = formatSeconds(elapsedSeconds);
        etaSource = 'elapsed';
    }
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": "job-status",
        className: `space-y-2 ${className}`,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center space-x-2",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                "data-testid": "job-status-badge",
                                "data-status": status || 'none',
                                className: `inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(status || 'none')}`,
                                children: getStatusText(status || 'none')
                            }),
                            isPolling && /*#__PURE__*/ _jsx("div", {
                                className: "h-3 w-3 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"
                            }),
                            isPolling && /*#__PURE__*/ _jsx("span", {
                                "data-testid": "job-status-elapsed",
                                "data-eta-source": etaSource,
                                className: "text-xs text-gray-500 dark:text-gray-400 tabular-nums",
                                children: remainingLabel
                            })
                        ]
                    }),
                    showCancelButton && /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: handleCancel,
                        disabled: isCancelling,
                        className: "text-xs text-red-600 hover:text-red-700 disabled:opacity-50 dark:text-red-400 dark:hover:text-red-300",
                        children: isCancelling ? 'Cancelling...' : 'Cancel'
                    })
                ]
            }),
            showProgressBar && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1.5",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 gap-3",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "flex-1 truncate",
                                children: progress.currentStep || 'Processing...'
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "font-medium flex-shrink-0",
                                children: [
                                    progress.percentage,
                                    "%"
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700",
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "h-full rounded-full bg-blue-500 transition-all duration-300 ease-in-out",
                            style: {
                                width: `${progress.percentage}%`
                            }
                        })
                    })
                ]
            }),
            isPolling && (!progress || progress.percentage === 0) && /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-gray-600 dark:text-gray-400",
                children: progress?.currentStep || 'Waiting to start...'
            }),
            jobStatus.error && status === 'failed' && /*#__PURE__*/ _jsx("div", {
                "data-testid": "job-status-error",
                className: "rounded-md bg-red-50 p-2 dark:bg-red-900/20",
                children: /*#__PURE__*/ _jsx("p", {
                    className: "text-xs text-red-800 dark:text-red-200",
                    children: jobStatus.error
                })
            }),
            status === 'completed' && /*#__PURE__*/ _jsx("p", {
                "data-testid": "job-status-success",
                className: "text-xs text-green-600 dark:text-green-400",
                children: "Processing completed successfully"
            })
        ]
    });
};
